import type { Ref } from 'react';
import styled, { css } from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';

const LogoWrapper = styled.div`
  width: var(--double-unit);
  height: var(--double-unit);
  flex: none;
  img {
    width: 100%;
    height: 100%;
    vertical-align: baseline;
    &:not([src]) {
      display: none;
    }
  }
`;

const ShortName = styled.span``;
const LongName = styled.span`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
const Score = styled.span`
  flex-shrink: 0;
`;

type TeamWrapperProps = {
  winner?: boolean;
  hasLost: boolean;
};
const TeamWrapper = styled(Horizontal).attrs({ gap: 0.5 })<TeamWrapperProps>`
  padding: var(--half-unit) var(--unit);
  width: 100%;
  ${SBHorizontal} {
    min-width: 0;
  }

  ${({ winner }) => {
    if (winner === false) {
      return css`
        opacity: 0.5;
      `;
    }
    if (winner === true) {
      return css`
        font-weight: bold;
      `;
    }
    return css``;
  }};

  ${({ winner, hasLost }) => {
    if (!hasLost && winner) {
      return css`
        background: rgba(255, 255, 255, 0.1);
      `;
    }

    return css``;
  }};
`;

export const GameCell = styled(Vertical).attrs({ gap: 0 })<{ big?: boolean }>`
  background-color: var(--c-nd-50);
  border-radius: var(--half-unit);

  --skew-angle: 15;
  transform: skew(calc(-1deg * var(--skew-angle)));
  ${TeamWrapper} {
    > * {
      transform: skew(calc(1deg * var(--skew-angle)));
    }
  }
  overflow: hidden;
  min-width: 50px;
  font-size: 10px;

  ${LongName} {
    display: none;
  }

  > * + * {
    border-top: 2px solid rgba(0, 0, 0, 0.2);
  }
  @container (width > 720px) {
    ${({ big }) =>
      big
        ? css`
            min-width: 110px;
            font-size: 14px;
            background-color: var(--c-nd-100);

            ${ShortName} {
              display: none;
            }
            ${LongName} {
              display: inherit;
            }
          `
        : css``}
  }
  @container (width > 1000px) {
    min-width: 110px;
    font-size: 12px;
    ${({ big }) =>
      big
        ? css`
            min-width: 150px;
            font-size: 18px;
            ${LogoWrapper} {
              width: var(--triple-unit);
              height: var(--triple-unit);
            }
            ${TeamWrapper} {
              gap: var(--unit);
            }
          `
        : ``}
    ${ShortName} {
      display: none;
    }
    ${LongName} {
      display: inherit;
    }
  }
`;

type Props = {
  team: TeamType | undefined;
  winner?: boolean;
  hasLost?: boolean;
  ref?: Ref<HTMLDivElement>;
};

export const Team = ({ team, winner, hasLost = false, ref }: Props) => {
  return (
    <TeamWrapper winner={winner} hasLost={hasLost} ref={ref}>
      <LogoWrapper>
        <img alt="" src={team?.pictureUrl} />
      </LogoWrapper>
      <SBHorizontal grow>
        <LongName>{team?.name}</LongName>
        <ShortName>{team?.shortName}</ShortName>
        <Score>{team?.score}</Score>
      </SBHorizontal>
    </TeamWrapper>
  );
};

type TeamType = {
  slug: string;
  name: string;
  shortName: string;
  pictureUrl: string;
  score?: number;
};
